/** The only action a key created from the admin screen is allowed to perform. */
export const LOOKUP_ENDPOINT = "/layout-by-ref";

const HOST_RE = /^[a-z0-9.-]+$/;

function isPartnerHost(host) {
  if (host === "localhost") return true;
  if (!host || !HOST_RE.test(host) || host.startsWith(".") || host.includes("..")) return false;
  return host.includes(".");
}

/**
 * Match the backend lookup: drop the protocol and any path, then lowercase.
 * A full URL pasted into the form must be stored as the bare host, or the
 * key will not find floor plans stamped with that host.
 */
export function normalizePartnerAddress(value) {
  return String(value || "")
    .trim()
    .replace(/^https?:\/\//i, "")
    .split("/")[0]
    .split("?")[0]
    .replace(/\.+$/, "")
    .toLowerCase();
}

export function partnerAddressWarning(host) {
  if (!host) return "";
  if (
    host === "localhost" ||
    host.endsWith(".magicfurnish.com")
  ) {
    return "";
  }
  return "Embed uploads are saved under the MagicFurnish address (for example serhant.magicfurnish.com), not the partner's public website. A key labeled with the public website will not find those floor plans.";
}

export function privilegedAccountWarning(user) {
  if (user?.role === "admin" || user?.role === "partnerAdmin") {
    return "This key will act as an admin account. Choose a normal account unless you mean to do that.";
  }
  return "";
}

export function accountLabel(user) {
  if (!user || typeof user !== "object") return "Unknown account";
  const email = user.email || user.name || "Account";
  if (user.role === "admin") return `${email} (admin)`;
  if (user.role === "partnerAdmin") return `${email} (partner admin)`;
  return email;
}

/**
 * @returns {{ ok: true, body: object } | { ok: false, errors: Record<string, string> }}
 */
export function buildAdminApiKeyBody({ userId, name, origin, expiresInDays, description }) {
  const errors = {};
  const trimmedName = String(name || "").trim();
  const trimmedDescription = String(description || "").trim();
  const host = normalizePartnerAddress(origin);
  const days = Number(expiresInDays);

  if (!userId) errors.userId = "Choose the account this key belongs to.";
  if (!trimmedName) errors.name = "Give the key a name.";
  if (!isPartnerHost(host)) {
    errors.origin = "Enter an address like serhant.magicfurnish.com.";
  }
  if (!Number.isInteger(days) || days < 1 || days > 3650) {
    errors.expiresInDays = "Enter a whole number of days, from 1 to 3650.";
  }
  if (trimmedDescription.length > 200) {
    errors.description = "Keep the note under 200 characters.";
  }

  if (Object.keys(errors).length > 0) return { ok: false, errors };

  return {
    ok: true,
    body: {
      userId: String(userId),
      name: trimmedName,
      origin: host,
      allowedEndpoints: [LOOKUP_ENDPOINT],
      expiresInDays: days,
      ...(trimmedDescription ? { description: trimmedDescription } : {}),
    },
  };
}

export function stripSecret(key) {
  if (!key || typeof key !== "object") return key;
  const { secret, ...rest } = key;
  return rest;
}

export function adminApiKeyListPath(includeInactive) {
  const inactive = includeInactive ? "&includeInactive=true" : "";
  return `/apikeys/admin/all?limit=100&page=1${inactive}`;
}

export function normalizeAdminApiKeyList(response) {
  if (!response || response.status !== "success" || !Array.isArray(response.data)) {
    throw new Error("Unexpected API key list");
  }
  return {
    keys: response.data.map(stripSecret),
    totalCount: response.pagination?.totalCount ?? response.data.length,
  };
}

export function formatKeyExpiry(expiresAt) {
  if (!expiresAt) return "Does not expire";
  const date = new Date(expiresAt);
  if (Number.isNaN(date.getTime())) return "Unknown expiry";
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

export function readApiError(error, fallback) {
  const data = error?.data;
  if (typeof data?.message === "string" && data.message) return data.message;
  if (typeof data?.error === "string" && data.error) return data.error;
  if (typeof error?.error === "string" && error.error) return error.error;
  return fallback;
}
